import { AxiosError, HttpStatusCode } from 'axios';
import { createContext, FC, ReactNode, useCallback, useContext, useEffect, useState } from 'react';

import { Navigate, redirect } from 'react-router';
import { setUser } from 'src/utils/sentry-helpers';
import { AccountRequests, User, UserConfigParamType, UserWithJWTToken } from 'statotest-api-requests';
import { JWTTokenManager } from 'statotest-api-requests/dist/helper';
import { isExtCharts } from '../../helpers/helpers';

// Mock user for external charts mode
const createMockUser = (): User => ({
    id: -1,
    firstName: 'External',
    lastName: 'Charts User',
    email: 'external@charts.com',
    phone: '',
    role: 'USER',
    roleId: 1,
    config: '[]',
    dateTimeAdd: new Date().toISOString(),
    isVerified: true,
});

interface UnauthenticatedUserContext {
    loading: boolean;
    userData: User | null;
    loginUser: (user: UserWithJWTToken) => Promise<void>;
    logoutUser: () => void;

    userConfig: UserConfigParamType[];
    updateUserConfig: (newConfig: UserConfigParamType[]) => Promise<boolean>;
}

interface AuthenticatedUserContext extends UnauthenticatedUserContext {
    userData: User,
}

// Create a context for user data
const UnauthenticatedUserContext = createContext<UnauthenticatedUserContext | null>(null);
const AuthenticatedUserContext = createContext<AuthenticatedUserContext | null>(null);

// Hook for pages that can have null user (login, register)
export const useUnauthenticatedUserContext = (): UnauthenticatedUserContext => {
    const context = useContext(UnauthenticatedUserContext);
    if (!context) {
        throw new Error("useUnauthenticatedUserContext must be used within UserProvider");
    }
    return context;
};

// Hook for authenticated pages (user is guaranteed to exist)
export const useAuthenticatedUserContext = (): AuthenticatedUserContext => {
    const context = useContext(AuthenticatedUserContext);
    if (!context) {
        throw new Error("useAuthenticatedUserContext must be used within AuthenticatedUserProvider");
    }
    return context;
};


// Provider for authenticated pages
export const AuthenticatedUserProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const { loading, userData, userConfig, updateUserConfig, loginUser, logoutUser } = useUnauthenticatedUserContext();

    // For external charts mode, use mock user
    if (isExtCharts) {
        const mockUser = createMockUser();
        const authenticatedContext: AuthenticatedUserContext = {
            loading: false,
            userData: mockUser,
            logoutUser: () => {
                console.info('External charts mode: logout is a no-op');
            },
            loginUser: (user: UserWithJWTToken) => {
                console.info('External charts mode: login is a no-op', user);
                return Promise.resolve();
            },
            userConfig: [],
            updateUserConfig: (newConfig: UserConfigParamType[]) => {
                console.info('External charts mode: updateUserConfig is a no-op', newConfig);
                return Promise.resolve(true);
            },
        };

        return (
            <AuthenticatedUserContext.Provider value={authenticatedContext}>
                {children}
            </AuthenticatedUserContext.Provider>
        );
    }

    // If user is null, don't render children
    if (!userData) {
        return (<Navigate to="/login" replace />);
    }

    const authenticatedContext: AuthenticatedUserContext = {
        loading,
        userData,
        logoutUser,
        loginUser,
        userConfig,
        updateUserConfig,
    };

    return (
        <AuthenticatedUserContext.Provider value={authenticatedContext}>
            {children}
        </AuthenticatedUserContext.Provider>
    );
};

export const UnauthenticatedUserProvider: FC<{ children: ReactNode }> = ({ children }) => {
    const [userData, setUserData] = useState<User | null>(null);
    const [userConfig, setUserConfig] = useState<UserConfigParamType[]>([]);
    const [loading, setLoading] = useState(true);

    const parseUserConfig = useCallback((stringConfig: string): UserConfigParamType[] | false => {
        let newUserConfig: UserConfigParamType[] = [];
        try {
            const parsedConfig = JSON.parse((stringConfig.trim().length < 2) ? "[]" : stringConfig) as UserConfigParamType[];
            if (Array.isArray(parsedConfig)) { newUserConfig = parsedConfig }
            setUserConfig(newUserConfig);
            return newUserConfig;
        } catch (error) {
            console.error("chosenSelectCompany - failed to parse userConfig - errro: ", error);
            return false;
        }
    }, [])

    const updateUserConfig = useCallback(async (newConfig: UserConfigParamType[]): Promise<boolean> => {
        if (!userData?.id) { return false; }
        try {
            const response = await AccountRequests.updateSelfAccount({ config: JSON.stringify(newConfig) });
            if (!response.config) { throw new Error("updateUserConfig - failed to update userConfig"); }
            const configInvalid = parseUserConfig(response.config);
            if (!configInvalid) { throw new Error("retrieved invalid config format"); }
            return true;
        }
        catch (error) {
            console.error("updateUserConfig - failed to update config - error: ", error);
            return false;
        }
    }, [parseUserConfig, userData?.id]);

    const loginUser = useCallback(async (user: UserWithJWTToken): Promise<void> => {
        if (!user.jwtToken) { throw new Error("loginUser - passed user doesn`t have jwtToken") }
        const { jwtToken, ...userWithoutToken } = user;
        localStorage.setItem("token", jwtToken);
        localStorage.setItem("userId", String(user.id));
        await JWTTokenManager.getInstance().forceRetrieveNewToken();
        setUserData(userWithoutToken);
        parseUserConfig(user.config ?? "[]");
        setUser({
            id: String(user.id),
            roleId: user.roleId,
            email: user.email,
            phone: user.phone,
        })
        redirect('/');
    }, [parseUserConfig]);

    const logoutUser = useCallback((): void => {
        console.info("Logging out user");
        localStorage.removeItem("token");
        localStorage.removeItem("userId");
        setUserData(null);
        setUserConfig([]);
        redirect("/login")
    }, []);

    useEffect((): void => {
        const retrieveToken = async (): Promise<string> => localStorage.getItem("token") ?? "";
        const refreshToken = async (): Promise<string> => {
            try {
                const result = await AccountRequests.refreshToken();
                localStorage.setItem("token", result.jwtToken);
                return result.jwtToken;
            } catch (err) {
                if (err instanceof AxiosError && err.status === HttpStatusCode.Unauthorized) {
                    logoutUser();
                }
                throw err;
            }
        }


        const init = async (): Promise<void> => {
            const jwtToken = localStorage.getItem("token");
            const userId = localStorage.getItem("userId");

            try {
                await JWTTokenManager.getInstance(retrieveToken, refreshToken).forceRetrieveNewToken();
                if (!jwtToken || !userId) { return; }
                const result = await AccountRequests.getMyAccount();
                await loginUser({ ...result, jwtToken, });
            }
            catch (error) { console.error("useUserDataContext - init failed - error: ", error); }
            finally { setLoading(false); }
            setLoading(false);
        }

        if (isExtCharts) {
            setLoading(false);
            return;
        }
        void init();
    }, [loginUser, logoutUser]);

    // You can define functions to set or update the user data here
    return (
        <UnauthenticatedUserContext.Provider
            value={{
                loading,
                userData,
                loginUser, logoutUser,
                userConfig,
                updateUserConfig,
            }}
        >
            {children}
        </UnauthenticatedUserContext.Provider>
    );
};
